'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useCustomAddressStore } from '@libs/common-next';
import type { Product, TailoredApiData } from '@libs/common-types';
import { getBopisFulfillmentStatus, getSddFulfillmentStatus, getSthFulfillmentStatus, resolveFulfillmentMethod } from '../../product-details';
import type { MiniPdpForm } from '../types';
export type MiniPdpFulfillmentProps = Readonly<{
  product: Product;
  tailored: TailoredApiData | null;
  isLoading: boolean;
}>;
export function useMiniPdpFulfillment(t0) {
  const $ = _c(57);
  const {
    product,
    tailored,
    isLoading
  } = t0;
  const {
    register,
    setValue
  } = useFormContext();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      name: "fulfilmentMethod"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const selectedMethod = useWatch(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      name: "selectedSku"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const selectedSku = useWatch(t2);
  const customAddress = useCustomAddressStore(_temp);
  const tailoredVariant = selectedSku ? tailored?.variants[selectedSku] : undefined;
  const bopis = tailoredVariant?.BOPIS;
  const sdd = tailoredVariant?.SDD;
  const sth = tailoredVariant?.STH;
  const isBopisAvailableForPurchase = product.deliveryMethods.BOPIS.availablePurchaseOptions.oneTime;
  const isBopisAvailableForStore = bopis?.storeId ? tailored?.bopisStoreData?.[bopis.storeId]?.offersBopis ?? true : true;
  const isBopisAvailable = isBopisAvailableForPurchase && isBopisAvailableForStore;
  const isSddAvailable = product.deliveryMethods.SDD.availablePurchaseOptions.oneTime;
  const isSthAvailable = product.deliveryMethods.STH.availablePurchaseOptions.oneTime;
  let t3;
  if ($[2] !== isBopisAvailable || $[3] !== isSddAvailable || $[4] !== isSthAvailable) {
    t3 = {
      bopis: isBopisAvailable,
      sdd: isSddAvailable,
      sth: isSthAvailable
    };
    $[2] = isBopisAvailable;
    $[3] = isSddAvailable;
    $[4] = isSthAvailable;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const availableMethods = t3;
  let t4;
  if ($[6] !== availableMethods || $[7] !== isLoading || $[8] !== selectedMethod || $[9] !== setValue) {
    t4 = () => {
      if (isLoading) {
        return;
      }
      const resolvedMethod = resolveFulfillmentMethod({
        availableMethods,
        selectedMethod
      });
      if (resolvedMethod !== selectedMethod) {
        setValue("fulfilmentMethod", resolvedMethod);
      }
    };
    $[6] = availableMethods;
    $[7] = isLoading;
    $[8] = selectedMethod;
    $[9] = setValue;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== isBopisAvailable || $[12] !== isLoading || $[13] !== isSddAvailable || $[14] !== isSthAvailable || $[15] !== selectedMethod || $[16] !== setValue) {
    t5 = [isLoading, selectedMethod, setValue, isBopisAvailable, isSddAvailable, isSthAvailable];
    $[11] = isBopisAvailable;
    $[12] = isLoading;
    $[13] = isSddAvailable;
    $[14] = isSthAvailable;
    $[15] = selectedMethod;
    $[16] = setValue;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  useEffect(t4, t5);
  const t6 = bopis?.availableStock ?? 0;
  const t7 = bopis?.altAvailableStock ?? 0;
  const t8 = product.deliveryMethods.BOPIS.purchaseQuantities?.min ?? 1;
  const t9 = Boolean(bopis?.storeId);
  const t10 = Boolean(product.pharmacy);
  let t11;
  if ($[18] !== t10 || $[19] !== t6 || $[20] !== t7 || $[21] !== t8 || $[22] !== t9) {
    t11 = getBopisFulfillmentStatus({
      availableStock: t6,
      altAvailableStock: t7,
      minimumCaseQty: t8,
      isStoreSelected: t9,
      isPharmacy: t10
    });
    $[18] = t10;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = selectedMethod === "bopis" && !isLoading && isBopisAvailable;
  const t13 = !isLoading && !isBopisAvailable;
  let t14;
  if ($[24] !== isLoading || $[25] !== t11 || $[26] !== t12 || $[27] !== t13) {
    t14 = {
      value: "bopis" as const,
      title: "Pick up in store",
      iconName: "store-outline" as const,
      statusTestId: "bopis-tile-status-message",
      status: t11,
      isSelected: t12,
      isDisabled: t13,
      isLoading
    };
    $[24] = isLoading;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const t15 = sdd?.availableStock ?? 0;
  const t16 = product.deliveryMethods.SDD.purchaseQuantities?.min ?? 1;
  const t17 = Boolean(sdd?.doorDashValidationSuccess);
  const t18 = Boolean(customAddress?.streetLine1);
  let t19;
  if ($[29] !== t15 || $[30] !== t16 || $[31] !== t17 || $[32] !== t18) {
    t19 = getSddFulfillmentStatus({
      availableStock: t15,
      minimumCaseQty: t16,
      isAvailable: t17,
      hasStreetAddress: t18
    });
    $[29] = t15;
    $[30] = t16;
    $[31] = t17;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  const t20 = selectedMethod === "sdd" && !isLoading && isSddAvailable;
  const t21 = !isLoading && !isSddAvailable;
  let t22;
  if ($[34] !== isLoading || $[35] !== t19 || $[36] !== t20 || $[37] !== t21) {
    t22 = {
      value: "sdd" as const,
      title: "Same-day delivery",
      iconName: "box-outline" as const,
      statusTestId: "sdd-tile-status-message",
      status: t19,
      isSelected: t20,
      isDisabled: t21,
      isLoading
    };
    $[34] = isLoading;
    $[35] = t19;
    $[36] = t20;
    $[37] = t21;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  const t23 = sth?.availableStock ?? 0;
  const t24 = product.deliveryMethods.STH.purchaseQuantities?.min ?? 1;
  const t25 = Boolean(product.pharmacy?.oos);
  let t26;
  if ($[39] !== t23 || $[40] !== t24 || $[41] !== t25) {
    t26 = getSthFulfillmentStatus({
      availableStock: t23,
      minimumCaseQty: t24,
      isPharmacyOutOfStock: t25
    });
    $[39] = t23;
    $[40] = t24;
    $[41] = t25;
    $[42] = t26;
  } else {
    t26 = $[42];
  }
  const t27 = selectedMethod === "sth" && !isLoading && isSthAvailable;
  const t28 = !isLoading && !isSthAvailable;
  let t29;
  if ($[43] !== isLoading || $[44] !== t26 || $[45] !== t27 || $[46] !== t28) {
    t29 = {
      value: "sth" as const,
      title: "Ship to me",
      iconName: "car-outline" as const,
      statusTestId: "stm-tile-status-message",
      status: t26,
      isSelected: t27,
      isDisabled: t28,
      isLoading
    };
    $[43] = isLoading;
    $[44] = t26;
    $[45] = t27;
    $[46] = t28;
    $[47] = t29;
  } else {
    t29 = $[47];
  }
  let t30;
  if ($[48] !== t14 || $[49] !== t22 || $[50] !== t29) {
    t30 = [t14, t22, t29];
    $[48] = t14;
    $[49] = t22;
    $[50] = t29;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  const fulfillmentMethods = t30;
  let t31;
  if ($[52] !== register) {
    t31 = register("fulfilmentMethod");
    $[52] = register;
    $[53] = t31;
  } else {
    t31 = $[53];
  }
  let t32;
  if ($[54] !== fulfillmentMethods || $[55] !== t31) {
    t32 = {
      fulfillmentMethods,
      register: t31
    };
    $[54] = fulfillmentMethods;
    $[55] = t31;
    $[56] = t32;
  } else {
    t32 = $[56];
  }
  return t32;
}
function _temp(state) {
  return state.address;
}